import { useMutation, useQueryClient } from "@tanstack/react-query";
import { documentService } from "@/features/documents/services/documentService";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type {
  Document,
  UpdateDocumentPermissionsRequest,
} from "@/types/document";

function invalidateDocuments(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
}

export interface UploadDocumentVariables {
  file: File;
  departmentId: string;
  name?: string;
  libraryId?: string;
  language?: string;
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation<Document, Error, UploadDocumentVariables>({
    mutationFn: ({ file, departmentId, name, libraryId, language }) =>
      documentService.upload(file, departmentId, name, libraryId, language),
    onSuccess: (res) => {
      invalidateDocuments(queryClient);
      toastMutationSuccess(res, { fallbackMessage: "تم رفع المستند بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => documentService.delete(id),
    onSuccess: (res) => {
      invalidateDocuments(queryClient);
      toastMutationSuccess(res, { fallbackMessage: "تم حذف المستند" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useUpdateDocumentPermissions() {
  const queryClient = useQueryClient();
  return useMutation<
    Document,
    Error,
    { id: string; data: UpdateDocumentPermissionsRequest }
  >({
    mutationFn: ({ id, data }) => documentService.updatePermissions(id, data),
    onSuccess: (res) => {
      invalidateDocuments(queryClient);
      toastMutationSuccess(res, { fallbackMessage: "تم تحديث الصلاحيات" });
    },
    onError: (error) => handleApiError(error),
  });
}
